/**
 * WP-06C-B — Resolution is verified by the backend, confirmed here.
 *
 * The dialog does exactly two things: it shows the governed resolution
 * conditions the authority stored, and it asks the user to attest to the ones
 * that are not yet verified. It NEVER decides whether the Loop is resolvable:
 * each attestation is a separate authenticated command, and the final `resolve`
 * is refused by PostgreSQL when its own requirements are not met. The refusal
 * message shown here comes from the categorical vocabulary, never from a
 * provider error.
 *
 * WP-A-UI — A condition the authority records as `requires_evidence` cannot be
 * attested here: the backend refuses `human_attestation` for it, so no checkbox
 * is offered. Instead the dialog points to the governed Evidence section, where
 * "Verify with evidence" issues the evidence-backed lifecycle command. The
 * dialog reads the verified state back from the authoritative projection.
 *
 * Free-text evidence is deliberately absent: the hosted record has no evidence
 * write path here, so offering a note would imply storage that does not exist.
 */

import { useState } from "react";
import { FileCheck2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { HostedLoopCondition } from "@/lib/loops/read/loopProjection";

export function HostedResolutionDialog({
  open,
  onOpenChange,
  conditions,
  busy,
  onConfirm,
  onGoToEvidence,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conditions: readonly HostedLoopCondition[];
  busy: boolean;
  /** Attest the listed conditions, then ask the backend to resolve. */
  onConfirm: (conditionIds: readonly string[]) => void;
  /** Take the person to the governed Evidence section for evidence-required conditions. */
  onGoToEvidence?: (() => void) | undefined;
}) {
  const unverified = conditions.filter((c) => c.verificationState !== "verified");
  /** Conditions a person may attest to here. Evidence-required ones are excluded. */
  const attestable = unverified.filter((c) => !c.requiresEvidence);
  /** Conditions that still need evidence-backed verification from the Evidence section. */
  const evidenceOutstanding = unverified.filter((c) => c.requiresEvidence);
  const [attested, setAttested] = useState<readonly string[]>([]);
  const allAttested = attestable.every((c) => attested.includes(c.conditionId));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setAttested([]);
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Is this actually finished?</DialogTitle>
          <DialogDescription>
            {evidenceOutstanding.length > 0
              ? "This isn’t ready to be marked resolved yet."
              : "It counts as resolved once the outcome below is confirmed — not just when the work felt done."}
          </DialogDescription>
        </DialogHeader>

        {conditions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing has been recorded yet about what would finish this, so it can’t be confirmed
            yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {conditions.map((condition) => {
              const verified = condition.verificationState === "verified";
              const needsEvidence = !verified && condition.requiresEvidence;
              return (
                <li
                  key={condition.conditionId}
                  className="flex items-start gap-2.5"
                  data-testid="resolution-condition"
                >
                  {needsEvidence ? (
                    <FileCheck2
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  ) : (
                    <Checkbox
                      id={`cond-${condition.conditionId}`}
                      checked={verified || attested.includes(condition.conditionId)}
                      disabled={verified || busy}
                      onCheckedChange={(checked) =>
                        setAttested((prev) =>
                          checked === true
                            ? [...prev, condition.conditionId]
                            : prev.filter((id) => id !== condition.conditionId),
                        )
                      }
                      className="mt-0.5"
                    />
                  )}
                  <label
                    htmlFor={needsEvidence ? undefined : `cond-${condition.conditionId}`}
                    className="text-sm leading-relaxed text-foreground"
                  >
                    {condition.statement}
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      {verified
                        ? "· already verified"
                        : needsEvidence
                          ? "· needs evidence (proof)"
                          : condition.requirement === "required"
                            ? "· still needs confirmation"
                            : "· optional"}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        {evidenceOutstanding.length > 0 ? (
          <div
            className="rounded-xl border border-border bg-surface px-3 py-2.5 text-sm text-foreground"
            role="status"
            data-testid="resolution-needs-evidence"
          >
            <span className="font-medium">Still needed:</span>{" "}
            {evidenceOutstanding.length === 1
              ? "one outcome must be verified with evidence before this can be marked resolved."
              : `${evidenceOutstanding.length} outcomes must be verified with evidence before this can be marked resolved.`}{" "}
            Add something that shows it happened in the Evidence section, link it, then choose
            “Verify with evidence”.
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Not yet
          </Button>
          {evidenceOutstanding.length > 0 && onGoToEvidence ? (
            <Button variant="secondary" onClick={onGoToEvidence} disabled={busy}>
              <FileCheck2 className="size-4" aria-hidden="true" />
              Go to evidence
            </Button>
          ) : null}
          <Button
            data-testid="loop-confirm-resolve"
            disabled={
              busy || conditions.length === 0 || !allAttested || evidenceOutstanding.length > 0
            }
            onClick={() => onConfirm(attested)}
          >
            <ShieldCheck className="size-4" aria-hidden="true" />
            Yes, mark it resolved
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
